import React, { useState, useEffect } from 'react';
import { useModal } from '../context/ModalDialogContext';

export default function AgentNetworkManager({ user, tenantConfig, isGlobalRole, isAdminRole }) {
  const modal = useModal();
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');

  const [activeSubTab, setActiveSubTab] = useState('agents'); // 'agents', 'templates', 'commissions', 'pool'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Universal Top Banner Slide-in Notification Observers
  useEffect(() => {
    if (successMsg) modal.notify(successMsg, 'success');
  }, [successMsg, modal]);

  useEffect(() => {
    if (error) modal.notify(error, 'error');
  }, [error, modal]);

  // 1. Agents Directory State
  const [agents, setAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState('ALL'); // 'ALL', 'PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED'
  const [selectedAgentForApproval, setSelectedAgentForApproval] = useState(null);
  const [approvalTemplateId, setApprovalTemplateId] = useState('');
  const [selectedAgentForTemplate, setSelectedAgentForTemplate] = useState(null);
  const [reassignTemplateId, setReassignTemplateId] = useState('');

  // 2. Contract Templates State
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    duration_months: 12,
    commission_rate_pct: 10.0,
    sample_allowance_per_month: 2,
    min_qos_rating: 3.5,
    max_sla_response_hours: 48
  });

  // 3. Commissions State
  const [commissions, setCommissions] = useState([]);
  const [payingCommission, setPayingCommission] = useState(null);
  const [payoutRefInput, setPayoutRefInput] = useState('');

  // 4. Operator Pool State
  const [poolOperators, setPoolOperators] = useState([]);
  const [assigningOrg, setAssigningOrg] = useState(null);
  const [assignAgentId, setAssignAgentId] = useState('');
  const [assignTemplateId, setAssignTemplateId] = useState('');

  const fetchHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Tenant-Domain': window.location.hostname
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Fetch templates
      const resT = await fetch(`${API_BASE}/api/v1/agent-network/templates`, { headers: fetchHeaders });
      if (resT.ok) {
        const dataT = await resT.json();
        setTemplates(dataT);
        if (dataT.length > 0 && !approvalTemplateId) {
          setApprovalTemplateId(dataT[0].id.toString());
        }
      }

      // 2. Fetch agents
      const resA = await fetch(`${API_BASE}/api/v1/agent-network/agents`, { headers: fetchHeaders });
      if (resA.ok) {
        const dataA = await resA.json();
        setAgents(dataA);
      }

      // 3. Fetch commissions
      const resC = await fetch(`${API_BASE}/api/v1/agent-network/commissions`, { headers: fetchHeaders });
      if (resC.ok) {
        const dataC = await resC.json();
        setCommissions(dataC);
      }

      // 4. Fetch pool
      const resP = await fetch(`${API_BASE}/api/v1/agent-network/operators/pool`, { headers: fetchHeaders });
      if (resP.ok) {
        const dataP = await resP.json();
        setPoolOperators(dataP);
      }
    } catch (err) {
      setError(err.message || 'Error loading agent network data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  // --- Actions ---

  const handleApproveAgent = async () => {
    if (!selectedAgentForApproval || !approvalTemplateId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/agents/${selectedAgentForApproval.id}/approve`, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify({ template_id: parseInt(approvalTemplateId, 10) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not approve agent.');
      showToast(data.message || 'Agent approved successfully.');
      setSelectedAgentForApproval(null);
      loadData();
    } catch (err) {
      modal.alert('Approval Error', err.message);
    }
  };

  const handleRejectAgent = async (agent) => {
    const ok = await modal.confirm('Decline Agent Application', `Are you sure you want to decline the agent application for ${agent.email}?`);
    if (!ok) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/agents/${agent.id}/reject`, {
        method: 'POST',
        headers: fetchHeaders
      });
      if (!res.ok) throw new Error('Could not decline agent application.');
      showToast('Agent application declined.');
      loadData();
    } catch (err) {
      modal.alert('Error', err.message);
    }
  };

  const handleAssignTemplate = async () => {
    if (!selectedAgentForTemplate || !reassignTemplateId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/agents/${selectedAgentForTemplate.id}/assign-template`, {
        method: 'PUT',
        headers: fetchHeaders,
        body: JSON.stringify({ template_id: parseInt(reassignTemplateId, 10) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not assign template.');
      showToast(data.message || 'Template assigned.');
      setSelectedAgentForTemplate(null);
      loadData();
    } catch (err) {
      modal.alert('Error', err.message);
    }
  };

  const handleIssueBonusSample = async (agent) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/agents/${agent.id}/issue-sample-coupon`, {
        method: 'POST',
        headers: fetchHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to issue sample coupon.');
      modal.alert('Sample Coupon Issued', `Successfully issued extra coupon code: ${data.coupon_code} for ${agent.full_name || agent.email}.`);
      loadData();
    } catch (err) {
      modal.alert('Error', err.message);
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      const endpoint = editingTemplate?.id
        ? `${API_BASE}/api/v1/agent-network/templates/${editingTemplate.id}`
        : `${API_BASE}/api/v1/agent-network/templates`;
      const method = editingTemplate?.id ? 'PUT' : 'POST';

      const res = await fetch(endpoint, {
        method,
        headers: fetchHeaders,
        body: JSON.stringify(templateForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save template.');
      showToast(editingTemplate?.id ? 'Template updated successfully.' : 'Template created successfully.');
      setEditingTemplate(null);
      loadData();
    } catch (err) {
      modal.alert('Template Error', err.message);
    }
  };

  const handleDeleteTemplate = async (tpl) => {
    const ok = await modal.confirm('Deactivate Template', `Are you sure you want to deactivate template "${tpl.name}"?`);
    if (!ok) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/templates/${tpl.id}`, {
        method: 'DELETE',
        headers: fetchHeaders
      });
      if (!res.ok) throw new Error('Could not deactivate template.');
      showToast('Template deactivated.');
      loadData();
    } catch (err) {
      modal.alert('Error', err.message);
    }
  };

  const handleMarkPaid = async () => {
    if (!payingCommission || !payoutRefInput.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/commissions/${payingCommission.id}/mark-paid`, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify({ payout_reference: payoutRefInput.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to mark paid.');
      showToast('Commission marked as paid.');
      setPayingCommission(null);
      setPayoutRefInput('');
      loadData();
    } catch (err) {
      modal.alert('Error', err.message);
    }
  };

  const handleAssignPoolOperator = async () => {
    if (!assigningOrg || !assignAgentId) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/operators/${assigningOrg.organization_id}/assign-agent`, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify({
          agent_id: parseInt(assignAgentId, 10),
          template_id: assignTemplateId ? parseInt(assignTemplateId, 10) : undefined
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to assign agent.');
      showToast(data.message || 'Operator assigned to agent.');
      setAssigningOrg(null);
      loadData();
    } catch (err) {
      modal.alert('Error', err.message);
    }
  };

  const exportCommissionsCsv = () => {
    if (commissions.length === 0) {
      modal.alert('Export Notice', 'No commissions available to export.');
      return;
    }
    const headers = ['Commission ID', 'Agent Name', 'Agent Email', 'Organization', 'Order ID', 'Order Total', 'Shipping Deducted', 'Commissionable Subtotal', 'Rate %', 'Commission Amount', 'Status', 'Paid At', 'Payout Reference', 'Date Created'];
    const rows = commissions.map(c => [
      c.id,
      `"${c.agent_name || ''}"`,
      `"${c.agent_email || ''}"`,
      `"${c.organization_name || ''}"`,
      c.order_id,
      c.order_total,
      c.shipping_deducted,
      c.commissionable_subtotal,
      c.commission_pct,
      c.commission_amount,
      c.status,
      c.paid_at || '',
      `"${c.payout_reference || ''}"`,
      c.created_at
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `agent_commissions_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtered agents
  const filteredAgents = agents.filter(a => {
    if (agentFilter === 'ALL') return true;
    return (a.agent_status || 'PENDING_APPROVAL') === agentFilter;
  });

  const pendingCount = agents.filter(a => (a.agent_status || 'PENDING_APPROVAL') === 'PENDING_APPROVAL').length;
  const activeCount = agents.filter(a => a.agent_status === 'ACTIVE').length;

  const totalLiability = commissions.filter(c => c.status === 'PENDING').reduce((acc, c) => acc + (c.commission_amount || 0), 0);
  const totalPaid = commissions.filter(c => c.status === 'PAID').reduce((acc, c) => acc + (c.commission_amount || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header & Sub-Tabs Navigation */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '1px solid var(--border-color)',
        paddingBottom: '16px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>💼</span>
            <span>Agent Network & Partner Program</span>
          </h2>
          <div style={{ fontSize: '13px', color: 'var(--text-mute)', marginTop: '4px' }}>
            Administer global agent contracts, manage vetted operator allocations, monitor QoS SLAs, and process monthly commission payouts.
          </div>
        </div>

        {/* Sub-Tabs Pills */}
        <div style={{ display: 'flex', gap: '8px', backgroundColor: 'rgba(255, 255, 255, 0.04)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
          <button
            onClick={() => setActiveSubTab('agents')}
            style={{
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: '600',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: activeSubTab === 'agents' ? 'var(--primary-color)' : 'transparent',
              color: activeSubTab === 'agents' ? '#ffffff' : 'var(--text-main)',
              transition: 'all 0.15s ease'
            }}
          >
            <span>Agents Directory</span>
            {pendingCount > 0 && (
              <span style={{
                backgroundColor: '#ef4444',
                color: '#fff',
                fontSize: '10px',
                padding: '1px 6px',
                borderRadius: '10px',
                fontWeight: '700'
              }}>
                {pendingCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveSubTab('templates')}
            style={{
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: '600',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: activeSubTab === 'templates' ? 'var(--primary-color)' : 'transparent',
              color: activeSubTab === 'templates' ? '#ffffff' : 'var(--text-main)',
              transition: 'all 0.15s ease'
            }}
          >
            Contract Templates
          </button>

          <button
            onClick={() => setActiveSubTab('commissions')}
            style={{
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: '600',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: activeSubTab === 'commissions' ? 'var(--primary-color)' : 'transparent',
              color: activeSubTab === 'commissions' ? '#ffffff' : 'var(--text-main)',
              transition: 'all 0.15s ease'
            }}
          >
            Commissions & Payouts
          </button>

          <button
            onClick={() => setActiveSubTab('pool')}
            style={{
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: '600',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: activeSubTab === 'pool' ? 'var(--primary-color)' : 'transparent',
              color: activeSubTab === 'pool' ? '#ffffff' : 'var(--text-main)',
              transition: 'all 0.15s ease'
            }}
          >
            <span>Unassigned Operator Pool</span>
            {poolOperators.length > 0 && (
              <span style={{
                backgroundColor: '#3b82f6',
                color: '#fff',
                fontSize: '10px',
                padding: '1px 6px',
                borderRadius: '10px',
                fontWeight: '700'
              }}>
                {poolOperators.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Success Toast */}
      {successMsg && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.4)',
          color: '#10b981',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          ✓ {successMsg}
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
          color: '#ef4444',
          fontSize: '13px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* ==================================================== */}
      {/* 1. AGENTS DIRECTORY SUB-TAB                          */}
      {/* ==================================================== */}
      {activeSubTab === 'agents' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Filter Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['ALL', 'PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED'].map(f => (
                <button
                  key={f}
                  onClick={() => setAgentFilter(f)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    cursor: 'pointer',
                    backgroundColor: agentFilter === f ? 'var(--text-main)' : 'rgba(255, 255, 255, 0.04)',
                    color: agentFilter === f ? 'var(--bg-color)' : 'var(--text-mute)'
                  }}
                >
                  {f === 'ALL' ? `All Agents (${agents.length})` : f.replace('_', ' ')}
                  {f === 'PENDING_APPROVAL' && pendingCount > 0 && ` (${pendingCount})`}
                </button>
              ))}
            </div>

            <button
              onClick={loadData}
              style={{
                padding: '6px 14px',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '6px',
                border: '1px solid var(--border-color)',
                cursor: 'pointer',
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                color: 'var(--text-main)'
              }}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : '🔄 Refresh List'}
            </button>
          </div>

          {/* Table */}
          <div style={{
            backgroundColor: 'var(--panel-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            overflowX: 'auto'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Agent</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Contract Terms</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>Sourced Operators</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>QoS Rating</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Commission Owed</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAgents.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ padding: '36px', textAlign: 'center', color: 'var(--text-mute)' }}>
                      No agents found for this filter.
                    </td>
                  </tr>
                ) : (
                  filteredAgents.map(ag => {
                    const status = ag.agent_status || 'PENDING_APPROVAL';
                    return (
                      <tr key={ag.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: '700', color: 'var(--text-main)' }}>{ag.full_name || `${ag.first_name || ''} ${ag.last_name || ''}`.trim() || ag.email}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>{ag.email}</div>
                          {ag.billing_country && (
                            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>📍 {ag.billing_country}</div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '700',
                            backgroundColor:
                              status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.15)' :
                              status === 'PENDING_APPROVAL' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            color:
                              status === 'ACTIVE' ? '#10b981' :
                              status === 'PENDING_APPROVAL' ? '#f59e0b' : '#ef4444'
                          }}>
                            {status.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '12px' }}>
                            {ag.agent_template_name || 'Standard Agreement'}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                            Base Rate: {ag.commission_rate_pct || 10.0}%
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <span style={{ fontWeight: '700', color: 'var(--text-main)', fontSize: '14px' }}>
                            {ag.active_operators_count || 0}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <span style={{ color: '#f59e0b', fontWeight: '700' }}>★ {ag.avg_qos_rating || 5.0}</span>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <div style={{ fontWeight: '700', color: ag.pending_commission > 0 ? '#10b981' : 'var(--text-mute)' }}>
                            ${(ag.pending_commission || 0).toFixed(2)}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                            Paid: ${(ag.paid_commission || 0).toFixed(2)}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                            {status === 'PENDING_APPROVAL' ? (
                              <>
                                <button
                                  onClick={() => setSelectedAgentForApproval(ag)}
                                  style={{
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    backgroundColor: '#10b981',
                                    color: '#ffffff',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    border: 'none',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Approve & Assign
                                </button>
                                <button
                                  onClick={() => handleRejectAgent(ag)}
                                  style={{
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    color: '#ef4444',
                                    border: '1px solid rgba(239, 68, 68, 0.3)',
                                    fontSize: '11px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Decline
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedAgentForTemplate(ag);
                                    setReassignTemplateId(ag.agent_template_id ? ag.agent_template_id.toString() : '');
                                  }}
                                  title="Change contract template"
                                  style={{
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid var(--border-color)',
                                    color: 'var(--text-main)',
                                    fontSize: '11px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Edit Terms
                                </button>
                                <button
                                  onClick={() => handleIssueBonusSample(ag)}
                                  title="Issue free sample coupon"
                                  style={{
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                    border: '1px solid rgba(59, 130, 246, 0.3)',
                                    color: '#3b82f6',
                                    fontSize: '11px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  + Sample
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* 2. CONTRACT TEMPLATES SUB-TAB                        */}
      {/* ==================================================== */}
      {activeSubTab === 'templates' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>Standard Partner Contract Templates</h3>
              <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px' }}>
                Defined durations and commission schedules automatically stamped onto sourced operators.
              </div>
            </div>
            <button
              onClick={() => {
                setEditingTemplate({});
                setTemplateForm({
                  name: '',
                  description: '',
                  duration_months: 12,
                  commission_rate_pct: 10.0,
                  sample_allowance_per_month: 2,
                  min_qos_rating: 3.5,
                  max_sla_response_hours: 48
                });
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                backgroundColor: 'var(--primary-color)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: '700',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              + Create Template
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {templates.map(tpl => (
              <div
                key={tpl.id}
                style={{
                  backgroundColor: 'var(--panel-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxShadow: 'var(--card-shadow)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>{tpl.name}</div>
                    {tpl.description && (
                      <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '4px' }}>{tpl.description}</div>
                    )}
                  </div>
                  <span style={{
                    backgroundColor: 'rgba(16, 185, 129, 0.15)',
                    color: '#10b981',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}>
                    {tpl.duration_months} Months
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', backgroundColor: 'rgba(255, 255, 255, 0.02)', padding: '10px', borderRadius: '6px', fontSize: '12px' }}>
                  <div>
                    <span style={{ color: 'var(--text-mute)' }}>Commission Rate:</span>
                    <div style={{ fontWeight: '700', color: 'var(--text-main)', fontSize: '14px' }}>{tpl.commission_rate_pct}%</div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-mute)' }}>Sample Allowance:</span>
                    <div style={{ fontWeight: '700', color: 'var(--text-main)', fontSize: '14px' }}>{tpl.sample_allowance_per_month} / mo</div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-mute)' }}>SLA Target:</span>
                    <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{tpl.max_sla_response_hours}h</div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-mute)' }}>Min QoS Rating:</span>
                    <div style={{ fontWeight: '600', color: '#f59e0b' }}>★ {tpl.min_qos_rating}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
                  <button
                    onClick={() => {
                      setEditingTemplate(tpl);
                      setTemplateForm({
                        name: tpl.name,
                        description: tpl.description || '',
                        duration_months: tpl.duration_months,
                        commission_rate_pct: tpl.commission_rate_pct,
                        sample_allowance_per_month: tpl.sample_allowance_per_month,
                        min_qos_rating: tpl.min_qos_rating,
                        max_sla_response_hours: tpl.max_sla_response_hours
                      });
                    }}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-main)',
                      fontSize: '11px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(tpl)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#ef4444',
                      fontSize: '11px',
                      cursor: 'pointer'
                    }}
                  >
                    Deactivate
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* 3. COMMISSIONS & PAYOUTS SUB-TAB                     */}
      {/* ==================================================== */}
      {activeSubTab === 'commissions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: '700' }}>Pending Liability</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#f59e0b', marginTop: '6px' }}>
                ${totalLiability.toFixed(2)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>Ready for monthly payout</div>
            </div>

            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: '700' }}>Total Paid YTD</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: '#10b981', marginTop: '6px' }}>
                ${totalPaid.toFixed(2)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>Settled partner earnings</div>
            </div>

            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: '700' }}>Active Partners</div>
              <div style={{ fontSize: '24px', fontWeight: '800', color: 'var(--primary-color)', marginTop: '6px' }}>
                {activeCount}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>Earning on operator orders</div>
            </div>
          </div>

          {/* Action Row */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              onClick={exportCommissionsCsv}
              style={{
                padding: '8px 14px',
                borderRadius: '6px',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid var(--border-color)',
                color: 'var(--text-main)',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>📥</span>
              <span>Export Payouts CSV</span>
            </button>
          </div>

          {/* Ledger Table */}
          <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Agent</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Operator / Org</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Order Subtotal</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Rate</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Commission</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {commissions.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ padding: '36px', textAlign: 'center', color: 'var(--text-mute)' }}>
                      No commission records yet. Commissions will automatically accrue as sourced operators place and pay for orders.
                    </td>
                  </tr>
                ) : (
                  commissions.map(c => (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-mute)' }}>
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{c.agent_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>{c.agent_email}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{c.organization_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Order #{c.order_id}</div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600' }}>
                        ${c.commissionable_subtotal.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: 'var(--text-mute)' }}>
                        {c.commission_pct}%
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '700', color: '#10b981' }}>
                        ${c.commission_amount.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '700',
                          backgroundColor: c.status === 'PAID' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                          color: c.status === 'PAID' ? '#10b981' : '#f59e0b'
                        }}>
                          {c.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {c.status === 'PENDING' ? (
                          <button
                            onClick={() => {
                              setPayingCommission(c);
                              setPayoutRefInput('');
                            }}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '4px',
                              backgroundColor: 'rgba(16, 185, 129, 0.1)',
                              border: '1px solid rgba(16, 185, 129, 0.3)',
                              color: '#10b981',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Mark Paid
                          </button>
                        ) : (
                          <span style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                            {c.payout_reference || 'Settled'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* 4. UNASSIGNED OPERATOR POOL SUB-TAB                  */}
      {/* ==================================================== */}
      {activeSubTab === 'pool' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>Unallocated Operator Pool</h3>
            <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px' }}>
              Operators released from voided QoS contracts or self-registered without an agent. As Tenant Admin, you can assign them to a top-performing partner.
            </div>
          </div>

          <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Organization</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Location</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Primary Contact</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {poolOperators.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ padding: '36px', textAlign: 'center', color: 'var(--text-mute)' }}>
                      🎉 The unallocated pool is empty! All operators are assigned to active agents.
                    </td>
                  </tr>
                ) : (
                  poolOperators.map(op => (
                    <tr key={op.organization_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '700', color: 'var(--text-main)' }}>{op.organization_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>ID #{op.organization_id}</div>
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--text-mute)' }}>
                        {op.billing_city ? `${op.billing_city}, ` : ''}{op.billing_country || 'Worldwide'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{op.contact_name || 'Operator'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>{op.contact_email}</div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          onClick={() => {
                            setAssigningOrg(op);
                            setAssignAgentId(agents.find(a => a.agent_status === 'ACTIVE')?.id?.toString() || '');
                            setAssignTemplateId('');
                          }}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            backgroundColor: 'var(--primary-color)',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: '700',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          Assign Agent
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- MODAL: Approve Agent --- */}
      {selectedAgentForApproval && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '440px'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)' }}>Approve & Onboard Agent</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', marginTop: '8px' }}>
              Approve <strong>{selectedAgentForApproval.full_name}</strong> ({selectedAgentForApproval.email}) and assign an official contract agreement:
            </p>

            <div style={{ marginTop: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)', display: 'block', marginBottom: '6px' }}>
                Select Contract Template:
              </label>
              <select
                value={approvalTemplateId}
                onChange={(e) => setApprovalTemplateId(e.target.value)}
                style={{
                  width: '100%', padding: '10px', borderRadius: '6px',
                  backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                  border: '1px solid var(--border-color)', color: 'var(--text-main)'
                }}
              >
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.duration_months} mos, {t.commission_rate_pct}% comm, {t.sample_allowance_per_month} samples/mo)
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
              <button
                onClick={() => setSelectedAgentForApproval(null)}
                style={{
                  padding: '8px 14px', borderRadius: '6px',
                  backgroundColor: 'transparent', border: '1px solid var(--border-color)',
                  color: 'var(--text-mute)', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApproveAgent}
                style={{
                  padding: '8px 16px', borderRadius: '6px',
                  backgroundColor: '#10b981', border: 'none',
                  color: '#fff', fontWeight: '700', cursor: 'pointer'
                }}
              >
                Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: Reassign Template --- */}
      {selectedAgentForTemplate && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '440px'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)' }}>Update Agent Contract Template</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', marginTop: '8px' }}>
              Assign a new template to <strong>{selectedAgentForTemplate.full_name}</strong> for newly sourced operators:
            </p>

            <div style={{ marginTop: '16px' }}>
              <select
                value={reassignTemplateId}
                onChange={(e) => setReassignTemplateId(e.target.value)}
                style={{
                  width: '100%', padding: '10px', borderRadius: '6px',
                  backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                  border: '1px solid var(--border-color)', color: 'var(--text-main)'
                }}
              >
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.duration_months} mos, {t.commission_rate_pct}% comm, {t.sample_allowance_per_month} samples/mo)
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
              <button
                onClick={() => setSelectedAgentForTemplate(null)}
                style={{
                  padding: '8px 14px', borderRadius: '6px',
                  backgroundColor: 'transparent', border: '1px solid var(--border-color)',
                  color: 'var(--text-mute)', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignTemplate}
                style={{
                  padding: '8px 16px', borderRadius: '6px',
                  backgroundColor: 'var(--primary-color)', border: 'none',
                  color: '#fff', fontWeight: '700', cursor: 'pointer'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: Create / Edit Template --- */}
      {editingTemplate && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px',
            maxHeight: '90vh', overflowY: 'auto'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)' }}>
              {editingTemplate.id ? 'Edit Contract Template' : 'Create Contract Template'}
            </h3>

            <form onSubmit={handleSaveTemplate} style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Template Name:</label>
                <input
                  type="text"
                  required
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g. Standard 12-Month Agent"
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: '6px',
                    backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                    border: '1px solid var(--border-color)', color: 'var(--text-main)'
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Description:</label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="Terms and eligibility..."
                  rows={2}
                  style={{
                    width: '100%', padding: '8px 10px', borderRadius: '6px',
                    backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                    border: '1px solid var(--border-color)', color: 'var(--text-main)'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Duration (Months):</label>
                  <input
                    type="number"
                    min="1"
                    max="120"
                    required
                    value={templateForm.duration_months}
                    onChange={(e) => setTemplateForm({ ...templateForm, duration_months: parseInt(e.target.value, 10) })}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Commission Rate (%):</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    required
                    value={templateForm.commission_rate_pct}
                    onChange={(e) => setTemplateForm({ ...templateForm, commission_rate_pct: parseFloat(e.target.value) })}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Monthly Sample Allowance:</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    required
                    value={templateForm.sample_allowance_per_month}
                    onChange={(e) => setTemplateForm({ ...templateForm, sample_allowance_per_month: parseInt(e.target.value, 10) })}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Target SLA (Hours):</label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={templateForm.max_sla_response_hours}
                    onChange={(e) => setTemplateForm({ ...templateForm, max_sla_response_hours: parseInt(e.target.value, 10) })}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                <button
                  type="button"
                  onClick={() => setEditingTemplate(null)}
                  style={{
                    padding: '8px 14px', borderRadius: '6px',
                    backgroundColor: 'transparent', border: '1px solid var(--border-color)',
                    color: 'var(--text-mute)', cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 18px', borderRadius: '6px',
                    backgroundColor: 'var(--primary-color)', border: 'none',
                    color: '#fff', fontWeight: '700', cursor: 'pointer'
                  }}
                >
                  Save Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- MODAL: Mark Paid Commission --- */}
      {payingCommission && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '440px'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)' }}>Record Commission Settlement</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', marginTop: '8px' }}>
              Mark commission of <strong>${payingCommission.commission_amount.toFixed(2)}</strong> to <strong>{payingCommission.agent_name}</strong> as settled:
            </p>

            <div style={{ marginTop: '16px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>
                Payment Reference / Transaction ID:
              </label>
              <input
                type="text"
                required
                value={payoutRefInput}
                onChange={(e) => setPayoutRefInput(e.target.value)}
                placeholder="e.g. PayPal TXN-89241, Wire #4012"
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: '6px',
                  backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                  border: '1px solid var(--border-color)', color: 'var(--text-main)'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setPayingCommission(null)}
                style={{
                  padding: '8px 14px', borderRadius: '6px',
                  backgroundColor: 'transparent', border: '1px solid var(--border-color)',
                  color: 'var(--text-mute)', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMarkPaid}
                style={{
                  padding: '8px 16px', borderRadius: '6px',
                  backgroundColor: '#10b981', border: 'none',
                  color: '#fff', fontWeight: '700', cursor: 'pointer'
                }}
              >
                Confirm Paid
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: Assign Pool Operator --- */}
      {assigningOrg && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '440px'
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)' }}>Assign Operator to Agent</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', marginTop: '8px' }}>
              Assign <strong>{assigningOrg.organization_name}</strong> to an approved agent partner:
            </p>

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Select Active Agent:</label>
                <select
                  value={assignAgentId}
                  onChange={(e) => setAssignAgentId(e.target.value)}
                  style={{
                    width: '100%', padding: '10px', borderRadius: '6px',
                    backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                    border: '1px solid var(--border-color)', color: 'var(--text-main)'
                  }}
                >
                  {agents.filter(a => a.agent_status === 'ACTIVE').map(a => (
                    <option key={a.id} value={a.id}>
                      {a.full_name} ({a.email}) - {a.billing_country || 'Worldwide'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Contract Template (Optional Override):</label>
                <select
                  value={assignTemplateId}
                  onChange={(e) => setAssignTemplateId(e.target.value)}
                  style={{
                    width: '100%', padding: '10px', borderRadius: '6px',
                    backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                    border: '1px solid var(--border-color)', color: 'var(--text-main)'
                  }}
                >
                  <option value="">Use Agent's Default Template</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.duration_months} mos, {t.commission_rate_pct}%)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
              <button
                onClick={() => setAssigningOrg(null)}
                style={{
                  padding: '8px 14px', borderRadius: '6px',
                  backgroundColor: 'transparent', border: '1px solid var(--border-color)',
                  color: 'var(--text-mute)', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignPoolOperator}
                style={{
                  padding: '8px 16px', borderRadius: '6px',
                  backgroundColor: 'var(--primary-color)', border: 'none',
                  color: '#fff', fontWeight: '700', cursor: 'pointer'
                }}
              >
                Assign & Bind Contract
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
